import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { HelpCircle } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import './FAQ.css';

const FAQ = () => {
  const faqCategories = [
    {
      category: 'General Questions',
      questions: [
        {
          question: 'How long has Moua\'s Auto Repair been in business?',
          answer: 'We\'ve been proudly serving the St. Paul community since 2015, building our reputation on honest service and quality workmanship.'
        },
        {
          question: 'What areas do you serve?',
          answer: 'We serve St. Paul and the surrounding east metro areas including Maplewood, Oakdale, Woodbury, West St. Paul, North St. Paul, Roseville, Little Canada, and nearby communities.'
        },
        {
          question: 'What types of vehicles do you work on?',
          answer: 'We service all makes and models of cars, trucks, and SUVs—both domestic and foreign vehicles. Our experienced team has the skills and equipment to handle a wide range of vehicles.'
        },
        {
          question: 'Are you a certified repair shop?',
          answer: 'Yes, our technicians are experienced and skilled in automotive repair. We use professional-grade equipment and follow industry best practices to ensure quality work.'
        }
      ]
    },
    {
      category: 'Services & Pricing',
      questions: [
        {
          question: 'Do you provide free estimates?',
          answer: 'Yes, we provide honest, transparent estimates for all repair work. We\'ll explain what needs to be done and give you a fair price before starting any work. There are no hidden fees or surprise charges.'
        },
        {
          question: 'Do you offer same-day service?',
          answer: 'Yes! For many repairs, we can provide same-day service. It depends on the type of repair and our current workload. Call us at (651) 772-3388 to discuss your specific needs and we\'ll do our best to accommodate your schedule.'
        },
        {
          question: 'What services do you offer?',
          answer: 'We offer comprehensive auto repair services including engine diagnostics, brake repair, suspension and steering repair, electrical systems, transmission service, body and bumper repair, cooling system service, AC and heating service, general maintenance, and more.'
        },
        {
          question: 'How do your prices compare to other shops?',
          answer: 'Our prices are fair, competitive, and transparent. We believe in honest pricing without hidden fees. Many customers tell us our rates are more affordable than dealerships and other shops, while maintaining high-quality work.'
        },
        {
          question: 'Do you use quality parts?',
          answer: 'Absolutely. We use reliable parts and components that meet or exceed manufacturer specifications. We can also discuss part options with you if you have preferences or budget considerations.'
        }
      ]
    },
    {
      category: 'Scheduling & Appointments',
      questions: [
        {
          question: 'Do I need an appointment?',
          answer: 'While appointments are recommended to ensure we can serve you promptly, we also accept walk-ins based on availability. For the best service, we suggest calling ahead at (651) 772-3388.'
        },
        {
          question: 'What are your business hours?',
          answer: 'We\'re open Monday through Friday from 9:00 AM to 6:30 PM, Saturday from 10:00 AM to 4:30 PM, and closed on Sunday. We\'re here to serve you six days a week.'
        },
        {
          question: 'How do I schedule a service appointment?',
          answer: 'You can call us directly at (651) 772-3388 or use the contact form on our website to request service. We\'ll get back to you promptly to confirm your appointment.'
        },
        {
          question: 'How long do repairs typically take?',
          answer: 'Repair time varies depending on the type of service needed. Simple repairs like brake pads or oil changes can often be done in a few hours. More complex repairs may take longer. We\'ll give you an estimated timeframe when we assess your vehicle.'
        }
      ]
    },
    {
      category: 'Payment & Warranty',
      questions: [
        {
          question: 'What payment methods do you accept?',
          answer: 'We accept cash, credit cards, and debit cards for your convenience. We\'ll provide you with a detailed invoice for all work performed.'
        },
        {
          question: 'Do you offer any warranties on your work?',
          answer: 'Yes, we stand behind our work. Warranty details vary depending on the type of service and parts used. We\'ll discuss warranty coverage with you before we begin any repair.'
        },
        {
          question: 'Can I get a quote over the phone?',
          answer: 'We can provide general pricing information over the phone, but for accurate estimates, we typically need to inspect your vehicle. This allows us to diagnose the issue properly and give you an honest, detailed quote.'
        }
      ]
    },
    {
      category: 'Other Questions',
      questions: [
        {
          question: 'Do you offer towing services?',
          answer: 'We don\'t provide towing services directly, but we can recommend reliable local towing companies if you need your vehicle transported to our shop.'
        },
        {
          question: 'Can I wait while my car is being serviced?',
          answer: 'For shorter repairs, you\'re welcome to wait at our facility. For longer repairs, we recommend arranging alternative transportation. We\'ll keep you updated on the progress and call you when your vehicle is ready.'
        },
        {
          question: 'Do you perform inspections for buying used cars?',
          answer: 'Yes, we can perform pre-purchase inspections to help you make informed decisions when buying a used vehicle. Call us to schedule an inspection appointment.'
        },
        {
          question: 'What should I do if my check engine light comes on?',
          answer: 'Don\'t panic! Bring your vehicle in as soon as you can. We\'ll use our diagnostic equipment to read the error codes and determine what\'s causing the light. Some issues are minor, while others need immediate attention. We\'ll give you an honest assessment.'
        },
        {
          question: 'Can I bring my own parts?',
          answer: 'We prefer to use parts we source to ensure quality and provide warranty coverage. However, we can discuss your specific situation. Keep in mind that we may not be able to warranty labor on customer-supplied parts.'
        }
      ]
    }
  ];

  return (
    <div className="faq-page">
      <Header />
      
      {/* Hero Section */}
      <section className="faq-hero">
        <div className="container">
          <div className="faq-hero-content text-center">
            <HelpCircle size={64} className="faq-hero-icon" />
            <h1 className="fade-in-up">Frequently Asked Questions</h1>
            <p className="faq-hero-subtitle fade-in-up">
              Quick answers to common questions about our services
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Categories */}
      <section className="section section-dark">
        <div className="container">
          <div className="faq-categories">
            {faqCategories.map((category, catIndex) => (
              <div key={catIndex} className="faq-category">
                <h2 className="faq-category-title">{category.category}</h2>
                <Accordion type="single" collapsible className="faq-accordion">
                  {category.questions.map((faq, qIndex) => (
                    <AccordionItem key={qIndex} value={`item-${catIndex}-${qIndex}`}>
                      <AccordionTrigger className="faq-question">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="faq-answer">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="section section-light">
        <div className="container">
          <div className="faq-cta text-center">
            <h2>Still Have Questions?</h2>
            <p className="section-subtitle">
              If you didn't find the answer you're looking for, we're happy to help. Give us a call or send us a message.
            </p>
            <div className="faq-cta-actions">
              <a href="tel:6517723388" className="btn-primary">
                Call (651) 772-3388
              </a>
              <a href="/contact" className="btn-secondary">
                Contact Us Online
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQ;
